import { useState } from "react";
import { Trash2, Plus, Save, RefreshCw, Printer, Sparkles, QrCode, Link as LinkIcon, Upload, Image as ImageIcon, User, X, ChevronDown, FileDown, Mail, CreditCard, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { money, emptyItem, STATUS, uploadFile, currencyLabel, currencySymbol, snapshotRates } from "../studioUtils";

export default function QuoteEditor({
  quote, setQuote, rate, setRate, rateInfo, loadRate,
  suggestions, setSuggestions, onProductEdit, save, revising, revisionOf, locked,
  totals, vat, savedRef, savedAt, ean13, publicUrl,
  onOpenQr, onCopyLink, onDownloadPdf, onSendToCustomer, onCreatePaymentLink, onSendWhatsApp, onCurrencyChange,
  customers, loadCustomers,
  company,
}) {
  const [showCustomers, setShowCustomers] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("");
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [lastPickedIndex, setLastPickedIndex] = useState(-1);
  const [lastPickedName, setLastPickedName] = useState("");
  const isTL = quote.currency === "TRY";
  const symbol = currencySymbol(quote.currency);
  const label = currencyLabel(quote.currency);
  const update = (key, value) => setQuote(q => ({ ...q, [key]: value }));
  const updateItem = (i, key, value) => {
    if (key === "product_name") {
      if (i === lastPickedIndex) {
        setLastPickedIndex(-1);
        setLastPickedName("");
      }
      onProductEdit && onProductEdit();
    }
    setQuote(q => ({ ...q, items: q.items.map((it, idx) => idx === i ? { ...it, [key]: value } : it) }));
  };

  const pickSuggestion = (i, s) => {
    const rates = snapshotRates({ ...quote, currency_rate: rate, usd_rate: quote.usd_rate || rateInfo.rate,
      eur_rate: quote.eur_rate || rateInfo.eur_rate, gbp_rate: quote.gbp_rate || rateInfo.gbp_rate });
    const from = s.price_currency || "USD";
    const conversion = from === quote.currency ? 1 : rates[from] / rates[quote.currency];
    if (!Number.isFinite(conversion) || conversion <= 0) return toast.error("Ürün fiyatını dönüştürmek için güncel kur gerekli.");
    setSuggestions([]);
    setLastPickedIndex(i);
    setLastPickedName(s.name);
    setQuote(q => ({
      ...q,
      items: q.items.map((it, idx) => idx === i
        ? { ...it, product_name: s.name, sku: s.sku || it.sku || "",
            list_price: Number(((s.default_price || 0) * conversion).toFixed(2)),
            image_url: s.image_url || it.image_url }
        : it),
    }));
  };

  const pickCustomer = (c) => {
    setQuote(q => ({
      ...q, customer_id: c.id, customer_name: c.company_name || c.name,
      customer_phone: c.phone || "", customer_email: c.email || "",
      customer_location: c.address || "", customer_tax_office: c.tax_office || "",
      customer_tax_number: c.tax_number || "", customer_contact: c.contact_person || "",
    }));
    setShowCustomers(false);
    toast.success(`Cari seçildi: ${c.name}`);
  };

  const uploadItemImage = async (i, file) => {
    if (!file) return;
    try {
      const result = await uploadFile(file);
      updateItem(i, "image_url", result.absoluteUrl);
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error("Görsel yüklenemedi");
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (!customerFilter) return true;
    const q = customerFilter.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) ||
           (c.company_name || "").toLowerCase().includes(q) ||
           (c.tax_number || "").includes(q);
  });
  const customerMatches = quote.customer_name.trim().length >= 2
    ? customers.filter(c => [c.company_name, c.name].some(value =>
        (value || "").toLocaleLowerCase("tr-TR").startsWith(quote.customer_name.trim().toLocaleLowerCase("tr-TR")))).slice(0, 8)
    : [];

  return (
    <section className="workspace">
      <div className="editor-column">
        {company.logo_url && (
          <div className="editor-logo-band" data-testid="editor-logo">
            <img src={company.logo_url} alt="logo" />
            <span>Bu logo teklif çıktısında ve müşteri linkinde görünür</span>
          </div>
        )}
        {revising && (
          <div className="revision-banner" data-testid="revision-banner">
            <b>Revize oluşturuluyor</b>
            <span>Ana teklif: <code>{revisionOf}</code> — kaydettiğinizde yeni bir Rev sürümü olarak eklenir</span>
          </div>
        )}
        {locked && (
          <div className="revision-banner locked-banner" data-testid="locked-banner">
            <b>🔒 Müşteri tarafından işlem gördü</b>
            <span>
              {quote.customer_approval?.name} tarafından {" "}
              <b>{quote.customer_approval?.decision === "onaylandi" ? "onaylandı" :
                   quote.customer_approval?.decision === "reddedildi" ? "reddedildi" : "beklemeye alındı"}</b>.
              Değişiklik yapmak için <b>Revize</b> oluşturun. Sertifika: <code>{quote.customer_approval?.certificate_id}</code>
            </span>
          </div>
        )}
        <div className="section-head">
          <div>
            <p className="eyebrow blue">ADIM 01 / MÜŞTERİ</p>
            <h2>Teklif kimin için?</h2>
          </div>
          <span className="date-badge" data-testid="auto-date-display">
            {new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
          </span>
        </div>
        <div className="customer-toolbar">
          <button data-testid="pick-customer-button" className="outline-button" onClick={() => { setShowCustomers(true); loadCustomers && loadCustomers(); }}>
            <User size={14} /> Cari listesinden seç
          </button>
          {quote.customer_id && (
            <span className="picked-chip">Cari kayıtlı <button className="link-button" onClick={() => update("customer_id", "")}><X size={12} /></button></span>
          )}
        </div>
        <div className="form-grid">
          <div className="customer-autocomplete"><label>Müşteri / Firma adı
            <input data-testid="customer-name-input" value={quote.customer_name}
              autoComplete="off" onFocus={() => { loadCustomers?.(); setShowCustomerSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 160)}
              onChange={e => { setQuote(q => ({ ...q, customer_name: e.target.value, customer_id: "" })); setShowCustomerSuggestions(true); }} placeholder="Firma adı" /></label>
            {showCustomerSuggestions && customerMatches.length > 0 && <div className="suggestions customer-suggestions">
              {customerMatches.map(c => <button type="button" key={c.id} onMouseDown={e => e.preventDefault()}
                onClick={() => { pickCustomer(c); setShowCustomerSuggestions(false); }}>
                <div className="sugg-text"><b>{c.company_name || c.name}</b><em>{[c.contact_person, c.tax_number].filter(Boolean).join(" · ")}</em></div>
              </button>)}
            </div>}
          </div>
          <label>Konu
            <input data-testid="quote-subject-input" value={quote.subject}
              onChange={e => update("subject", e.target.value)} placeholder="Teklif konusu" />
          </label>
          <label>Yetkili kişi
            <input data-testid="customer-contact-input" value={quote.customer_contact}
              onChange={e => update("customer_contact", e.target.value)} placeholder="Ad Soyad" />
          </label>
          <label>Telefon
            <input data-testid="customer-phone-input" value={quote.customer_phone}
              onChange={e => update("customer_phone", e.target.value)} placeholder="05xx xxx xx xx" />
          </label>
          <label>E-posta
            <input data-testid="customer-email-input" value={quote.customer_email}
              onChange={e => update("customer_email", e.target.value)} placeholder="ornek@firma.com" />
          </label>
          <label>Vergi dairesi
            <input data-testid="customer-tax-office-input" value={quote.customer_tax_office}
              onChange={e => update("customer_tax_office", e.target.value)} placeholder="Ör. Kadıköy V.D." />
          </label>
          <label>Vergi / TC no
            <input data-testid="customer-tax-number-input" value={quote.customer_tax_number}
              onChange={e => update("customer_tax_number", e.target.value)} placeholder="10 / 11 hane" />
          </label>
          <label>Adres
            <input data-testid="customer-location-input" value={quote.customer_location}
              onChange={e => update("customer_location", e.target.value)} placeholder="Semt / şehir" />
          </label>
        </div>
        <div className="section-head items-head">
          <div>
            <p className="eyebrow blue">ADIM 02 / KALEMLER</p>
            <h2>Ürün ve hizmetler</h2>
          </div>
          <div className="items-toolbar">
            <label className="toggle-inline" data-testid="show-images-toggle">
              <input type="checkbox" checked={!!quote.show_images}
                onChange={e => update("show_images", e.target.checked)} />
              <span>Görselleri teklifte göster</span>
            </label>
            <span className="memory-hint"><Sparkles size={14} /> Ortak hafıza aktif</span>
          </div>
        </div>
        <div className="items-table">
          <div className={`table-row table-head ${quote.show_images ? "with-image" : ""}`}>
            {quote.show_images && <span>Görsel</span>}
            <span>Ürün / hizmet açıklaması</span>
            <span>Stok kodu</span>
            <span>Adet</span><span data-testid="item-price-currency-label">Liste fiyatı {label}</span>
            <span>İskonto %</span><span>Tutar {isTL ? "TL" : `${label} / TL`}</span><span></span>
          </div>
          {quote.items.map((item, i) => {
            const itemTotal = Number(item.quantity || 0) * Number(item.list_price || 0) * (1 - Number(item.discount || 0) / 100);
            return (
              <div className={`table-row ${quote.show_images ? "with-image" : ""}`} key={i}>
                {quote.show_images && (
                  <label className="item-image-slot" data-testid={`item-image-slot-${i}`}>
                    {item.image_url ? (
                      <img src={item.image_url} alt="" />
                    ) : (
                      <span className="item-image-placeholder"><ImageIcon size={16} /></span>
                    )}
                    <input type="file" accept="image/*" hidden
                      onChange={e => uploadItemImage(i, e.target.files?.[0])} />
                  </label>
                )}
                <div className="product-field">
                  <input data-testid={`product-name-input-${i}`} value={item.product_name}
                    onChange={e => updateItem(i, "product_name", e.target.value)} placeholder="Ürün adı yazın..." />
                  {i === quote.items.length - 1 && suggestions.length > 0
                    && !(lastPickedIndex === i && lastPickedName === item.product_name) && (
                    <div className="suggestions">
                      {suggestions.map(s => (
                        <button data-testid={`product-suggestion-${s.name}`} key={s.name}
                          onClick={() => pickSuggestion(i, s)}>
                          {s.image_url && <img src={s.image_url} alt="" className="suggestion-thumb" />}
                          <div className="sugg-text">
                            <b>{s.name}</b>
                            {s.sku && <em>{s.sku}</em>}
                          </div>
                          <small>{s.default_price ? money(s.default_price, currencySymbol(s.price_currency)) : ""}</small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <label className="item-number-field"><span>Stok kodu</span><input aria-label="Stok kodu" data-testid={`sku-input-${i}`} value={item.sku || ""}
                  onChange={e => updateItem(i, "sku", e.target.value)} placeholder="SKU-001" /></label>
                <label className="item-number-field"><span>Adet</span><input aria-label="Adet" data-testid={`quantity-input-${i}`} type="number" min="0" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} /></label>
                <label className="item-number-field"><span>Liste fiyatı {label}</span><input aria-label={`Liste fiyatı ${label}`} data-testid={`list-price-input-${i}`} type="number" min="0" step="0.01" value={item.list_price} onFocus={e => e.target.select()} onChange={e => updateItem(i, "list_price", e.target.value)} /></label>
                <label className="item-number-field"><span>İskonto %</span><input aria-label="İskonto" data-testid={`discount-input-${i}`} type="number" min="0" max="100" value={item.discount} onFocus={e => e.target.select()} onChange={e => updateItem(i, "discount", e.target.value)} /></label>
                <strong data-testid={`line-total-display-${i}`}>{money(itemTotal, symbol)}{!isTL && <small>{money(itemTotal * rate)}</small>}</strong>
                <button data-testid={`remove-item-button-${i}`} className="icon-button"
                  onClick={() => setQuote(q => ({ ...q, items: q.items.filter((_, idx) => idx !== i) }))} title="Satırı sil">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
          <button data-testid="add-item-button" className="add-line" onClick={() => update("items", [...quote.items, emptyItem()])}>
            <Plus size={15} /> Yeni satır ekle
          </button>
        </div>
        <div className="details-band">
          <label>Teslimat süresi
            <input data-testid="delivery-terms-input" value={quote.delivery_terms} onChange={e => update("delivery_terms", e.target.value)} />
          </label>
          <label>Ödeme şekli
            <input data-testid="payment-terms-input" value={quote.payment_terms} onChange={e => update("payment_terms", e.target.value)} />
          </label>
          <label>Geçerlilik (gün)
            <input data-testid="validity-days-input" type="number" value={quote.validity_days} onChange={e => update("validity_days", e.target.value)} />
          </label>
          <label>Durum
            <select data-testid="status-select" value={quote.status || "taslak"} onChange={e => update("status", e.target.value)}>
              {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label>Takip tarihi
            <input data-testid="follow-up-date-input" type="date"
              value={quote.follow_up_date || ""} onChange={e => update("follow_up_date", e.target.value)} />
          </label>
          <label className="full-field">Not
            <input data-testid="notes-input" value={quote.notes} onChange={e => update("notes", e.target.value)} />
          </label>
        </div>
      </div>
      <aside className="summary-column">
        <div className="summary-card">
          <div className="summary-label">TEKLİF ÖZETİ</div>
          <div className="ref-box">
            <span>{savedRef ? "Kayıtlı referans" : "Otomatik referans (kayıttan sonra)"}</span>
            <strong data-testid="reference-preview">
              {savedRef || `${company.ref_prefix || "KNX"}-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}-••••`}
            </strong>
            {ean13 && <small className="ean-hint">EAN13: {ean13}</small>}
          </div>
          <div className="summary-lines">
            {!isTL && <div><span>Ara toplam {label}</span><b data-testid="subtotal-usd-display">{money(totals.usd, symbol)}</b></div>}
            <div><span>Ara toplam TL</span><b data-testid="subtotal-display">{money(totals.tl)}</b></div>
            <div><span>KDV %{quote.vat_rate}</span><b data-testid="vat-display">{money(vat)}</b></div>
            <div className="grand"><span>Genel toplam TL</span><strong data-testid="grand-total-display">{money(totals.tl + vat)}</strong></div>
          </div>
          <div className="rate-detail">
            <div className="rate-mode-tabs" data-testid="rate-mode-tabs">
              <button type="button" className={`rate-mode-tab ${(quote.rate_mode || "manual") === "manual" ? "active" : ""}`}
                disabled={isTL} onClick={() => update("rate_mode", "manual")} data-testid="rate-mode-manual">
                Manuel
              </button>
              <button type="button" className={`rate-mode-tab ${quote.rate_mode === "auto" ? "active" : ""}`}
                disabled={isTL} onClick={() => { update("rate_mode", "auto"); loadRate(); }} data-testid="rate-mode-auto">
                Otomatik
              </button>
            </div>
            <label className="currency-picker">Para birimi
              <select data-testid="quote-currency" value={quote.currency || "USD"}
                onChange={e => onCurrencyChange(e.target.value)}>
                <option value="TRY">TL — Türk Lirası</option>
                <option value="USD">USD — Dolar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — İngiliz Sterlini</option>
              </select>
            </label>
            <div><span>Uygulanan kur ({label}/₺)</span><b data-testid="manual-rate-value">{rate.toFixed(2)} ₺</b></div>
            {rateInfo.eur_rate ? <div><span>EUR/₺</span><b>{Number(rateInfo.eur_rate).toFixed(2)} ₺</b></div> : null}
            {rateInfo.gbp_rate ? <div><span>GBP/₺</span><b>{Number(rateInfo.gbp_rate).toFixed(2)} ₺</b></div> : null}
            <label className="manual-rate-label">
              {isTL ? "TL / TL (sabit)" : quote.rate_mode === "auto" ? "Kur (otomatik + marj)" : "Manuel kur"}
              <input data-testid="manual-rate-input" type="number" min="0" step="0.01" value={rate}
                disabled={isTL || quote.rate_mode === "auto"}
                onChange={e => { const n = Number(e.target.value); if (Number.isFinite(n)) setRate(n); }} />
            </label>
            {!isTL && <small>
              {rateInfo.stale ? "Otomatik servis beklemede · iş kuralı aktif" : `Güncellendi · ${rateInfo.date || "bugün"}`}
              <br />
              {(() => {
                const m = Number(company.rate_margin ?? 0.4);
                const r = company.rate_rounding || "0.1";
                if (r === "none") return `+${m.toFixed(2)} ₺ marj uygulanır`;
                return `+${m.toFixed(2)} ₺ marj · ${r} adıma yuvarlanır`;
              })()}
              {quote.rate_mode === "auto" && <><br /><b style={{ color: "#059669" }}>Kayıt sırasında güncel kur alınır ve marj uygulanır.</b></>}
              {" "}<button data-testid="fetch-selected-rate" className="link-button-inline" onClick={loadRate}><RefreshCw size={11} /> Otomatik kurdan çek</button>
            </small>}
          </div>
          <button data-testid="save-quote-button" className="primary-button" onClick={save} disabled={locked && !revising}>
            <Save size={16} /> {locked && !revising ? "Kilitli · Revize aç" : revising ? "Revize olarak kaydet" : "Teklifi Kaydet"}
          </button>
          <button data-testid="print-quote-button" className="outline-button" onClick={() => window.print()}>
            <Printer size={16} /> Teklifi yazdır / PDF al
          </button>
          {savedRef && (
            <>
              <button data-testid="show-qr-button" className="outline-button" onClick={onOpenQr}>
                <QrCode size={16} /> QR & Paylaşım linki
              </button>
              <button data-testid="copy-link-button" className="outline-button" onClick={onCopyLink}>
                <LinkIcon size={16} /> Linki kopyala
              </button>
              <button data-testid="download-pdf-button" className="outline-button" onClick={onDownloadPdf}>
                <FileDown size={16} /> PDF indir
              </button>
              <button data-testid="send-whatsapp-button" className="outline-button" onClick={onSendWhatsApp}>
                <MessageCircle size={16} /> WhatsApp ile gönder
              </button>
              {quote.customer_email && (
                <button data-testid="send-to-customer-button" className="outline-button" onClick={onSendToCustomer}>
                  <Mail size={16} /> Müşteriye Gönder
                </button>
              )}
              {(company.payment_provider && company.payment_provider !== "none") && (
                <button data-testid="create-payment-link-button" className="outline-button" onClick={onCreatePaymentLink}>
                  <CreditCard size={16} /> Ödeme Linki Oluştur
                </button>
              )}
              {quote.payment_link && (
                <a data-testid="payment-link-preview" className="outline-button pay-link-inline"
                   href={quote.payment_link} target="_blank" rel="noopener noreferrer">
                  <CreditCard size={16} /> Ödeme Linki Açık →
                </a>
              )}
            </>
          )}
        </div>
      </aside>

      {showCustomers && (
        <div className="modal-overlay" onClick={() => setShowCustomers(false)}>
          <div className="modal-card lg" onClick={e => e.stopPropagation()} data-testid="customer-picker">
            <button className="modal-close" onClick={() => setShowCustomers(false)}><X size={16} /></button>
            <h2>Cari seç</h2>
            <input placeholder="Cari adı, firma, vergi no ile ara" autoFocus
              value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}
              data-testid="customer-picker-search" className="picker-search" />
            <div className="picker-list">
              {filteredCustomers.length === 0 ? (
                <div className="picker-empty">Kayıtlı cari yok. Cari listesi menüsünden ekleyebilirsiniz.</div>
              ) : filteredCustomers.map(c => (
                <button data-testid={`customer-pick-${c.id}`} key={c.id} className="picker-row"
                  onClick={() => pickCustomer(c)}>
                  <div>
                    <b>{c.company_name || c.name}</b>
                    <span>{c.contact_person ? `${c.contact_person} · ` : ""}{c.phone} {c.email && `· ${c.email}`}</span>
                  </div>
                  <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
